import type { ReactNode } from "react";

/**
 * Kanonik Üst Bar yerleşimi (CLOS-98 `[059]`).
 *
 * ### Sıra DEĞİŞTİRİLEMEZ
 *
 * Kart kabul kriteri olarak sabitliyor: Sidebar Kontrolü, Çalışma Alanı, Arama, Yeni
 * Sözleşme, Bekleyen İşlemler, Bildirimler, Profil. Sıra bu dizide duruyor ve bir test
 * onu kartın metniyle karşılaştırıyor — biri yuvaları yeniden dizerse yakalanır.
 *
 * ### Dolmayan yuva ÇİZİLMEZ
 *
 * `[057]`'nin gezinme kaydıyla aynı yaklaşım: Arama `[061]`, Yeni Sözleşme `[062]`,
 * Bekleyen İşlemler `[063]` ve Bildirimler `[064]` henüz yok. Yer tutucu koymak
 * kullanıcıyı tıklayınca hiçbir şey olmayan bir düğmeye götürürdü ve ADR-0014'ü ihlal
 * ederdi. Kart da buna izin veriyor: *"CLOS-99–CLOS-104 ... daha sonra bu yerleşime
 * bağlar; CLOS-98 onların tamamlanmasına bağlı değildir."*
 *
 * ### Davetler BİLEREK YOK
 *
 * Negatif kriter: *"Davetler kalıcı Üst Bar öğesi değildir."* Kullanıcı tarafındaki
 * kanonik girişi `[065]` Profil menüsü. Bir test bunu sabitliyor.
 *
 * ### `packages/ui`'DE, `apps/web/src/layouts`'ta DEĞİL (UI Tur 2 §10.5)
 *
 * Bu bileşen zaten domain BİLMİYORDU — yalnız yuva doldurma/sıra mantığı taşıyor,
 * hangi bileşenin hangi yuvaya girdiği (`WorkspaceSwitcher`, `ProfileMenu`…) çağıranın
 * işi. Kabuk kalıbı (yükseklik, boşluk) mockup'ın her ekranında aynı; bu yüzden kit'e
 * taşındı. Ölçüler §4: 80px yükseklik, öğeler arası 16px.
 */

/** ⚠ KANONİK SIRA — kart kabul kriterinden birebir. */
export const TOP_BAR_SLOTS = [
  "sidebarControl",
  "workspace",
  "search",
  "newContract",
  "pendingTasks",
  "notifications",
  "profile",
] as const;

export type TopBarSlot = (typeof TOP_BAR_SLOTS)[number];

export interface TopBarProps {
  /**
   * Yuvalar. Verilmeyen ya da `null` olan yuva render EDİLMEZ; sırası korunur ki ilgili
   * kart geldiğinde tek satır değişsin.
   */
  slots: Partial<Record<TopBarSlot, ReactNode>>;
}

export function TopBar({ slots }: TopBarProps) {
  const dolu = TOP_BAR_SLOTS.filter((slot) => slots[slot] != null);

  /**
   * Sidebar Kontrolü ve Çalışma Alanı SOLDA, kalanlar sağda. Kart kanonik SIRAYI
   * sabitliyor, hizalamayı değil; bu ayrım gezinme bağlamını (nerede olduğum) aksiyonlardan
   * (ne yapabilirim) görsel olarak ayırıyor.
   */
  const solYuvalar: readonly TopBarSlot[] = ["sidebarControl", "workspace"];
  const sol = dolu.filter((s) => solYuvalar.includes(s));
  const sag = dolu.filter((s) => !solYuvalar.includes(s));

  /**
   * ⚠ ARAMA ORTADA ve BÜYÜYOR.
   *
   * Sıra dizideki hâliyle korunuyor (kart onu sabitliyor) ama arama yuvası kalan bütün
   * genişliği alıyor: onaylı tasarımda üst barın merkezinde geniş bir arama alanı var.
   * Sağa yığılmış küçük bir arama simgesi, kartın "Arama üst barın kanonik öğesidir"
   * kararını görselde ikinci sınıf bir ikona indirgiyordu.
   */
  const aramaVar = sag.includes("search");
  const sagKalan = sag.filter((s) => s !== "search");

  return (
    <header className="flex min-h-16 shrink-0 flex-wrap items-center gap-2 border-b border-border-default bg-surface px-3 py-2 sm:h-20 sm:flex-nowrap sm:gap-4 sm:px-6 sm:py-0">
      <div className="flex min-w-0 shrink-0 items-center gap-2">
        {sol.map((slot) => (
          <div key={slot} className="flex min-w-0 items-center">
            {slots[slot]}
          </div>
        ))}
      </div>

      {aramaVar ? (
        <div className="order-3 flex w-full min-w-0 flex-none justify-center sm:order-none sm:w-auto sm:flex-1">
          <div className="w-full max-w-xl">{slots.search}</div>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="flex shrink-0 items-center gap-2">
        {sagKalan.map((slot) => (
          <div key={slot} className="flex items-center">
            {slots[slot]}
          </div>
        ))}
      </div>
    </header>
  );
}
